import {categoryUrlBuilder, productUrlBuilder} from '@salesforce/retail-react-app/app/utils/url'
import {pageUrlBuilder} from './url-utils'

/**
 * Supported SFCC search-redirect types.
 *
 * These mirror the `type` values exported by the SFCC job `[MRT] Export Search
 * Redirects`. `pipeline` refers to legacy SiteGenesis pipelines which do not
 * exist in PWA Kit and are therefore intentionally unsupported.
 *
 * @readonly
 * @enum {string}
 */
export const SEARCH_REDIRECT_TYPES = {
    CATEGORY: 'category',
    PRODUCT: 'product',
    PAGE: 'page',
    URL: 'url',
    PIPELINE: 'pipeline'
}

/**
 * Safely parses the raw `SEARCH_REDIRECTS` environment variable value.
 *
 * The value is a JSON string keyed by SFCC site id. Any parsing error or
 * unexpected shape resolves to an empty object so callers can treat the feature
 * as simply "no redirects configured".
 *
 * @param {string} raw - The raw JSON string (typically `process.env.SEARCH_REDIRECTS`).
 * @returns {Object<string, Array>} Parsed object keyed by site id, or `{}`.
 */
export const parseSearchRedirects = (raw) => {
    if (!raw || typeof raw !== 'string') {
        return {}
    }

    try {
        const parsed = JSON.parse(raw)
        return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {}
    } catch {
        return {}
    }
}

/**
 * Extracts the search-redirect entries for a single site.
 *
 * @param {string} raw - The raw `SEARCH_REDIRECTS` JSON string.
 * @param {string} siteId - The active SFCC site id (e.g. `VBQ-EU`).
 * @returns {Array<{term: string, redirect: string, type: string}>} The site's
 *          redirect entries, or an empty array when none are configured.
 */
export const getSiteSearchRedirects = (raw, siteId) => {
    if (!siteId) {
        return []
    }

    const entries = parseSearchRedirects(raw)[siteId]
    return Array.isArray(entries) ? entries : []
}

/**
 * Finds a redirect entry whose keywords match the submitted search query.
 *
 * Each entry's `term` is a comma-separated list of trigger keywords (as exported
 * from the SFCC BM redirect `keywords` field, e.g. `"new arrivals, what's new"`).
 * Matching mirrors SFCC behaviour: the query must equal one of the keywords
 * case-insensitively, trimmed and as an exact full-phrase (never a substring or
 * per-word match).
 *
 * @param {Array<{term: string}>} redirects - The site's redirect entries.
 * @param {string} term - The submitted search query.
 * @returns {Object|null} The matching entry, or `null` when there is no match.
 */
export const findSearchRedirect = (redirects, term) => {
    if (!Array.isArray(redirects) || typeof term !== 'string') {
        return null
    }

    const normalizedTerm = term.trim().toLowerCase()
    if (!normalizedTerm) {
        return null
    }

    return (
        redirects.find(
            (entry) =>
                typeof entry?.term === 'string' &&
                entry.term
                    .split(',')
                    .some((keyword) => keyword.trim().toLowerCase() === normalizedTerm)
        ) ?? null
    )
}

/**
 * Determines whether a URL is absolute (or protocol-relative).
 *
 * @param {string} url - The URL to test.
 * @returns {boolean} `true` when the URL points to an external origin.
 */
const isAbsoluteUrl = (url) => /^(https?:)?\/\//i.test(url)

/**
 * Resolves a matched redirect entry into a navigation descriptor.
 *
 * @param {Object} entry - A matched redirect entry.
 * @param {string} entry.redirect - The target identifier (meaning depends on `type`).
 * @param {string} entry.type - One of {@link SEARCH_REDIRECT_TYPES}.
 * @returns {{path: string, external: boolean}|null} The navigation descriptor,
 *          or `null` when the redirect cannot be handled in PWA Kit (e.g. a
 *          legacy `pipeline` redirect) and the normal search flow should run.
 */
export const resolveSearchRedirect = (entry) => {
    if (!entry?.redirect || !entry?.type) {
        return null
    }

    switch (entry.type) {
        case SEARCH_REDIRECT_TYPES.CATEGORY:
            return {path: categoryUrlBuilder({id: entry.redirect}), external: false}
        case SEARCH_REDIRECT_TYPES.PRODUCT:
            return {path: productUrlBuilder({id: entry.redirect}), external: false}
        case SEARCH_REDIRECT_TYPES.PAGE:
            return {path: pageUrlBuilder(entry.redirect), external: false}
        case SEARCH_REDIRECT_TYPES.URL: {
            const target = typeof entry.redirect === 'string' ? entry.redirect.trim() : ''
            if (!target) {
                return null
            }
            return {path: target, external: isAbsoluteUrl(target)}
        }
        case SEARCH_REDIRECT_TYPES.PIPELINE:
        default:
            // Legacy SiteGenesis pipelines don't exist in PWA Kit. Return null so
            // the caller falls back to the standard search-results flow.
            return null
    }
}
